import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Product, AddToCartEvent } from '../../../../shared/models/product.model';
import { Category } from '../../../../shared/models/category.model';
import {
  CategoryProductsTab,
  TabbedCarouselBlockComponent,
} from '../tabbed-carousel-block/tabbed-carousel-block.component';
import { PublicNewArrivalsSection } from '../../data/home-blocks.model';

export interface NewReleasesBlockData {
  limit_per_category?: number;
  sections: PublicNewArrivalsSection[];
  // Productos ya adaptados a `Product` UI, alineados 1:1 con `sections`. El
  // padre (HomeComponent) hace el mapeo via `publicOfferProductToUiProduct`
  // y lo pasa aquí para no acoplar el componente al shape API.
  sectionsUiProducts: Product[][];
}

/**
 * Bloque "Novedades": tabs por categoría madre con productos recientes en cada
 * una. Render dumb — el padre arma la data desde el bloque `new-arrivals` del
 * payload `/api/web/home` y la pasa por `input`.
 */
@Component({
  selector: 'app-new-releases-block',
  imports: [TabbedCarouselBlockComponent],
  template: `
    <app-tabbed-carousel-block
      title="Novedades"
      ariaLabel="Filtrar novedades por categoría"
      viewAllLabel="Ver catálogo"
      viewAllLink="/tienda"
      surface="primary"
      [tabs]="tabs()"
      [shouldRender]="shouldRender()"
      (addToCart)="addToCart.emit($event)"
      (zoomOpen)="zoomOpen.emit($event)"
      (notifyRequest)="notifyRequest.emit($event)"
      (notifyCancel)="notifyCancel.emit($event)"
      (cardClick)="cardClick.emit($event)"
      (reserveClick)="reserveClick.emit($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewReleasesBlockComponent {
  readonly data = input<NewReleasesBlockData | null>(null);

  // Construye los tabs del bloque a partir de las secciones del API. Cada
  // sección aporta su `PublicCategory` (con `is_all` adicional) — el front la
  // proyecta al shape `Category` que el tabbed component espera. Los productos
  // ya vienen mapeados desde el padre vía `sectionsUiProducts` para no
  // re-adaptar en cada CD.
  protected readonly tabs = computed<CategoryProductsTab[]>(() => {
    const value = this.data();
    if (!value) return [];
    return value.sections.map((section, idx): CategoryProductsTab => ({
      category: toUiCategory(section.category),
      products: value.sectionsUiProducts[idx] ?? [],
    }));
  });

  protected readonly shouldRender = computed(() => this.tabs().length > 0);

  addToCart = output<AddToCartEvent>();
  zoomOpen = output<Product>();
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  cardClick = output<Product>();
  reserveClick = output<Product>();
}

// El tabbed carousel tipa `category` como `Category` (del modelo del catálogo),
// pero las secciones del API exponen `PublicCategory & { is_all }`. Adaptador
// estrecho: rellena los campos requeridos con defaults seguros (no se usan en
// el render del tab, solo `id` y `name`).
function toUiCategory(c: PublicNewArrivalsSection['category']): Category {
  return {
    id: c.id,
    name: c.name,
    slug: c.slug ?? '',
    description: null,
    cover: c.cover,
    is_expansion: false,
    parent_id: null,
  };
}
